import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { useI18n } from "@/i18n";
import { useAuth } from "@/context/AuthContext";
import { Card, CardContent } from "@/components/ui/card";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

export default function Auth() {
  const { t, lang } = useI18n();
  const { user, login, register } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const [tab, setTab] = useState("login");
  const [busy, setBusy] = useState(false);
  const [login_, setLogin] = useState({ email: "", password: "" });
  const refFromUrl = params.get("ref") || localStorage.getItem("ciao_ref") || "";
  const [reg, setReg] = useState({ name: "", email: "", password: "", phone: "", ref: refFromUrl });

  useEffect(() => {
    if (!user) return;
    const incomplete = !user.profile_complete;
    navigate(incomplete ? "/profile?tab=info&welcome=1" : "/dashboard");
  }, [user, navigate]);

  const doLogin = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await login(login_.email, login_.password);
      toast.success(t("auth.loginSuccess"));
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error");
    } finally { setBusy(false); }
  };

  const doRegister = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await register(reg);
      localStorage.removeItem("ciao_ref");
      toast.success(t("auth.registerSuccess"));
      toast.info(lang === "zh" ? "请完善个人资料（出生年月日时），以生成命盘与每日运势" : "Please complete your profile (birth date & time) to unlock your chart & daily fortune");
      // redirect to /profile is handled by the effect (new users have no birth data)
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error");
    } finally { setBusy(false); }
  };

  return (
    <div className="max-w-md mx-auto px-4 py-12">
      <div className="text-center mb-6">
        <img src="/assets/logo.png" alt="CiAO Merit" className="mx-auto h-20 w-20 rounded-full ring-1 ring-[hsl(var(--brand-gold))]" />
        <h1 className="mt-3 font-display text-2xl font-bold">{t("nav.member")}</h1>
      </div>
      <Card className="border-border">
        <CardContent className="p-6">
          <Tabs value={tab} onValueChange={setTab}>
            <TabsList className="grid grid-cols-2 w-full mb-4">
              <TabsTrigger value="login" data-testid="tab-login">{t("auth.loginTitle")}</TabsTrigger>
              <TabsTrigger value="register" data-testid="tab-register">{t("auth.registerTitle")}</TabsTrigger>
            </TabsList>
            <TabsContent value="login">
              <form className="space-y-3" onSubmit={doLogin}>
                <div><Label>{t("auth.email")}</Label><Input type="email" required value={login_.email} onChange={(e) => setLogin({ ...login_, email: e.target.value })} data-testid="login-email" /></div>
                <div><Label>{t("auth.password")}</Label><Input type="password" required value={login_.password} onChange={(e) => setLogin({ ...login_, password: e.target.value })} data-testid="login-password" /></div>
                <Button type="submit" disabled={busy} className="w-full bg-[hsl(var(--primary))] text-primary-foreground" data-testid="login-submit">{t("common.login")}</Button>
              </form>
              <p className="mt-4 text-center text-sm text-muted-foreground">{t("auth.noAccount")}{" "}
                <button className="text-[hsl(var(--brand-vermillion))] font-semibold" onClick={() => setTab("register")}>{t("auth.toRegister")}</button>
              </p>
            </TabsContent>
            <TabsContent value="register">
              <form className="space-y-3" onSubmit={doRegister}>
                <div><Label>{t("auth.name")}</Label><Input required value={reg.name} onChange={(e) => setReg({ ...reg, name: e.target.value })} data-testid="reg-name" /></div>
                <div><Label>{t("auth.email")}</Label><Input type="email" required value={reg.email} onChange={(e) => setReg({ ...reg, email: e.target.value })} data-testid="reg-email" /></div>
                <div><Label>{t("auth.password")}</Label><Input type="password" required value={reg.password} onChange={(e) => setReg({ ...reg, password: e.target.value })} data-testid="reg-password" /></div>
                <div><Label>{t("auth.phone")}</Label><Input value={reg.phone} onChange={(e) => setReg({ ...reg, phone: e.target.value })} data-testid="reg-phone" /></div>
                <div><Label>{t("auth.refCode")}</Label><Input value={reg.ref} onChange={(e) => setReg({ ...reg, ref: e.target.value })} data-testid="reg-ref" /></div>
                <Button type="submit" disabled={busy} className="w-full bg-[hsl(var(--primary))] text-primary-foreground" data-testid="register-submit">{t("common.register")}</Button>
              </form>
              <p className="mt-4 text-center text-sm text-muted-foreground">{t("auth.hasAccount")}{" "}
                <button className="text-[hsl(var(--brand-vermillion))] font-semibold" onClick={() => setTab("login")}>{t("auth.toLogin")}</button>
              </p>
            </TabsContent>
          </Tabs>
        </CardContent>
      </Card>
    </div>
  );
}
